import { useCallback, useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'next-i18next'

import {
  fetchFacilityRecommendations,
  type FacilityRecommendationsParams,
} from 'features/facility/services/facilityRecommendationsApi'
import type { Facility } from 'features/facility/types/facility'

export type FetchFacilityRecommendationsParams = Omit<FacilityRecommendationsParams, 'locale'>

export const useFacilityRecommendations = () => {
  const { i18n } = useTranslation()
  const [facilities, setFacilities] = useState<Facility[]>([])
  const [isLoading, setLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const fetchRecommendations = useCallback(
    async (params: FetchFacilityRecommendationsParams) => {
      setLoading(true)
      setIsError(false)

      try {
        const data = await fetchFacilityRecommendations({
          ...params,
          locale: i18n.language,
        })
        setFacilities(data)
      } catch (error) {
        setIsError(true)
        setFacilities([])
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
          tags: { hook: 'useFacilityRecommendations' },
          extra: {
            ...params,
            locale: i18n.language,
          },
        })
      } finally {
        setLoading(false)
      }
    },
    [i18n.language]
  )

  return {
    facilities,
    isLoading,
    isError,
    fetchRecommendations,
  }
}
